import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SwPartNumber } from '../../../../../shared/models/sw-part-number/sw-part-number';
import { SwPartNumberApiService } from '../../../../services/api/sw-part-number-api.service';
import { AccessRightsService } from '../../../../services/access-rights.service';
import { Policy } from '../../../../../shared/models/info/policy';
import { UnsavedChangesTrackerService } from '../../../../services/unsaved-changes-tracker.service';
import { STORAGE_KEYS } from '../../../../../shared/constants/storage-keys';
import { TextAreaComponent } from '../../../common/text-area/text-area.component';
import { SwPartNumberUpdateConfirmation } from '../../../../../shared/models/sw-part-number/sw-part-number-update-confirmation';
import { AppInsightsService } from '../../../../services/app-insights.service';
import { FloppyIconComponent } from '../../../icons/floppy-icon/floppy-icon.component';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { HotToastService } from '@ngxpert/hot-toast';

@Component({
  selector: 'app-edit-sw-part-number',
  imports: [FormsModule, ReactiveFormsModule, TextAreaComponent, FloppyIconComponent, AgentActionDirective],
  templateUrl: './edit-sw-part-number.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './edit-sw-part-number.component.scss',
})
export class EditSwPartNumberComponent implements OnInit, OnChanges {
  @Input() public swPartNumber?: SwPartNumber;
  @Output() public updatedSwPartNumber = new EventEmitter<SwPartNumberUpdateConfirmation>();

  public swPartNumberForm!: FormGroup;
  public internalNotes!: FormControl;

  public editDisabled = true;

  private swPartNumberApiService = inject(SwPartNumberApiService);
  private toast = inject(HotToastService);
  private accessRightsService = inject(AccessRightsService);
  private unsavedChangesService = inject(UnsavedChangesTrackerService);
  private appInsightsService = inject(AppInsightsService);

  public ngOnInit(): void {
    this.init();
    this.appInsightsService.logSidebarUsage('SPN_edit');
  }

  public ngOnChanges(): void {
    this.init();
  }

  public update(): void {
    this.updateValuesFromForm();
    this.swPartNumberApiService.updateSwPartNumber(this.swPartNumber!).subscribe({
      next: (updatedSwPartNumber) => {
        this.updatedSwPartNumber.emit(updatedSwPartNumber);
        this.toast.success('SW-Part-Number updated successfully');
        this.swPartNumberForm.markAsPristine();
        this.swPartNumber!.rowVersion = updatedSwPartNumber.rowVersion;
      },
      error: () => {
        console.error('Error updating SW-Part-Number');
      },
    });
  }

  private init(): void {
    this.initForm();
    this.accessRightsService.getAccessRights$().subscribe((accessRights) => {
      this.editDisabled = !accessRights.authorized(Policy.canUpdateBasic);
    });
    this.setFormValues();
    this.swPartNumberForm.valueChanges.subscribe(() => {
      this.saveUnsavedChangesToSessionStorage();
    });
  }

  private initForm(): void {
    this.internalNotes = new FormControl(null, []);
    this.swPartNumberForm = new FormGroup({
      internalNotes: this.internalNotes,
    });
  }

  private setFormValues(): void {
    if (this.swPartNumber) {
      this.swPartNumberForm.patchValue({
        internalNotes: this.swPartNumber.internalNotes,
      });
    }
  }

  private updateValuesFromForm(): void {
    if (this.swPartNumber) {
      this.swPartNumber.internalNotes = this.swPartNumberForm.value.internalNotes;
    }
  }

  private saveUnsavedChangesToSessionStorage(): void {
    if (this.swPartNumberForm) {
      this.updateValuesFromForm();
      this.unsavedChangesService.saveUnsavedChangesToSessionStorage(
        STORAGE_KEYS.UNSAVED_SW_PART_NUMBER,
        this.swPartNumber
      );
    }
  }
}
